import React, { useState, useEffect } from 'react'
import { categoryAPI } from '../../services/api'

const CategoryBrowser = ({ onCategorySelect }) => {
  const [activeCategory, setActiveCategory] = useState('all')
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Load categories from backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await categoryAPI.getAll({ type: 'template', isActive: true, child: false })

        if (response?.data?.success) {
          const backendCategories = Array.isArray(response?.data?.data) ? response.data.data : []

          // Add "All" category at the beginning
          const allCategories = [
            {
              id: 'all',
              name: 'All',
              icon: '📋',
              color: 'bg-blue-100 text-blue-700',
              description: 'All templates'
            },
            ...backendCategories.map(cat => {
              try {
                return {
                  id: String(cat?.id || cat?._id || cat?.name || 'unknown').toLowerCase().replace(/\s+/g, '-'),
                  name: String(cat?.name || 'Unnamed'),
                  icon: getCategoryIcon(String(cat?.name || '')),
                  color: String(cat?.color || getRandomColor()),
                  description: String(cat?.description || '')
                }
              } catch (err) {
                console.error('Error processing category:', cat, err)
                return {
                  id: 'unknown-' + Math.random(),
                  name: 'Unnamed',
                  icon: '📁',
                  color: 'bg-gray-100 text-gray-600',
                  description: ''
                }
              }
            })
          ]

          setCategories(allCategories)
        } else {
          setError(response?.data?.message || 'Failed to fetch categories')
          // Fallback to default categories
          setCategories(getDefaultCategories())
        }
      } catch (error) {
        console.error('Error fetching categories:', error)
        setError('Failed to load categories')
        // Fallback to default categories
        setCategories(getDefaultCategories())
      } finally {
        setLoading(false)
      }
    }

    fetchCategories()
  }, [])

  // Helper function to get random color for categories
  const getRandomColor = () => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-green-100 text-green-700',
      'bg-pink-100 text-pink-700',
      'bg-purple-100 text-purple-700',
      'bg-red-100 text-red-700',
      'bg-indigo-100 text-indigo-700',
      'bg-yellow-100 text-yellow-700',
      'bg-gray-100 text-gray-700'
    ]
    return colors[Math.floor(Math.random() * colors.length)]
  }

  // Helper function to get icon based on category name
  const getCategoryIcon = (name) => {
    const iconMap = {
      'business': '💼',
      'social media': '📱',
      'education': '📚',
      'video': '🎥',
      'marketing': '📈',
      'print': '🖨️',
      'wedding': '💒',
      'birthday': '🎂',
      'event': '🎉',
      'logo': '🏷️',
      'poster': '📄',
      'flyer': '📋',
      'card': '💳',
      'banner': '🏳️',
      'presentation': '📊'
    }

    const lowerName = name.toLowerCase()
    for (const [key, icon] of Object.entries(iconMap)) {
      if (lowerName.includes(key)) {
        return icon
      }
    }
    return '📁' // Default icon
  }

  // Default categories fallback
  const getDefaultCategories = () => [
    {
      id: 'all',
      name: 'All',
      icon: '📋',
      color: 'bg-blue-100 text-blue-700'
    },
    {
      id: 'business',
      name: 'Business',
      icon: '💼',
      color: 'bg-green-100 text-green-700'
    },
    {
      id: 'social-media',
      name: 'Social Media',
      icon: '📱',
      color: 'bg-pink-100 text-pink-700'
    },
    {
      id: 'education',
      name: 'Education',
      icon: '📚',
      color: 'bg-purple-100 text-purple-700'
    },
    {
      id: 'video',
      name: 'Video',
      icon: '🎥',
      color: 'bg-red-100 text-red-700'
    },
    {
      id: 'marketing',
      name: 'Marketing',
      icon: '📈',
      color: 'bg-indigo-100 text-indigo-700'
    },
    {
      id: 'print',
      name: 'Print',
      icon: '🖨️',
      color: 'bg-gray-100 text-gray-700'
    },
    {
      id: 'cards',
      name: 'Cards',
      icon: '💌',
      color: 'bg-rose-100 text-rose-700'
    }
  ]

  const handleCategoryClick = (category_id) => {
    setActiveCategory(category_id)
    onCategorySelect && onCategorySelect(category_id)
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap gap-2">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="h-10 w-24 bg-gray-200 rounded-full animate-pulse"></div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Filter Tabs */}
      <div className="overflow-x-auto">
        <div className="flex gap-2 pb-2" style={{ minWidth: 'max-content' }}>
          {Array.isArray(categories) && categories.map((category) => {
            try {
              // Ensure all values are strings/primitives, not objects
              const categoryId = String(category?.id || 'unknown')
              const categoryName = String(category?.name || 'Unnamed')
              const categoryIcon = String(category?.icon || '📁')
              const categoryColor = String(category?.color || 'bg-gray-100 text-gray-600')

              return (
                <button
                  key={categoryId}
                  onClick={() => handleCategoryClick(categoryId)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeCategory === categoryId
                      ? categoryColor + ' shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span className="mr-2">{categoryIcon}</span>
                  {categoryName}
                </button>
              )
            } catch (err) {
              console.error('Error rendering category:', category, err)
              return null
            }
          })}
        </div>
      </div>
    </div>
  )
}

export default CategoryBrowser
